import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useJWTAuth } from '../context/JWTAuthContext';
import { useServerConfig } from '../context/ServerConfigContext';
import { formatGradeDisplay } from '../utils/gradeSystem';
import { hasMenuPermission, isAdminUser, shouldShowAdminMenu, getUserGradeString } from '../utils/menuPermissions';
import LoginPage from '../pages/LoginPage';
import './Navigation.css';

const Navigation = ({ showSettings = true }) => {
  const { user, isAuthenticated, logout, isLoading } = useJWTAuth();
  const { config, isConfigLoaded } = useServerConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 현재 페이지 결정
  const getCurrentPage = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/board')) return 'board';
    if (pathname.startsWith('/travel')) return 'travel';
    if (pathname === '/admin') return 'admin';
    if (pathname === '/menu-management') return 'menu-management';
    if (pathname === '/profile') return 'profile';
    if (pathname === '/login') return 'login';
    if (pathname === '/signup') return 'signup';
    return 'board';
  };
  
  const currentPage = getCurrentPage();

  // 사용자 역할 확인 (role 필드 기반)
  const isAdmin = isAdminUser(user);

  // 모든 사용 가능한 메뉴 항목
  const allMenuItems = [
    { id: 'travel', label: '여행 플래너', icon: '✈️' },
    { id: 'board', label: '게시판', icon: '📋' }
  ];

  // 관리자 전용 메뉴 항목
  const adminMenuItems = [];

  // 현재 사용자의 등급 확인
  const currentUserGrade = isAuthenticated ? user?.grade : null; // 비로그인 사용자는 null
  const gradeString = getUserGradeString(currentUserGrade); // null이면 'guest'로 변환됨

  // 네비게이션 항목 구성
  let navItems = [];

  // 항상 메뉴를 구성하되, config가 없으면 기본 설정 사용
  console.log(`🧭 [Navigation] 네비게이션 구성: 인증=${isAuthenticated}, 등급=${gradeString}, 설정로딩=${isConfigLoaded}`);

  // 기본 메뉴들 중 권한이 있는 것만 필터링 (config가 없으면 기본 권한 사용)
  navItems = allMenuItems.filter(item => hasMenuPermission(currentUserGrade, item.id, config, user));

  // 관리자인 경우 관리자 전용 메뉴 추가
  if (isAuthenticated && shouldShowAdminMenu(user)) {
    const adminMenusWithPermission = adminMenuItems.filter(item => hasMenuPermission(currentUserGrade, item.id, config, user));
    navItems = [...navItems, ...adminMenusWithPermission];

    // showSettings가 true인 경우 관리자 페이지 메뉴 추가
    if (showSettings) {
      navItems = [...navItems, { id: 'admin', label: '관리자 페이지', icon: '⚙️' }];
    }
  }

  console.log(`📋 [Navigation] 최종 네비게이션 메뉴: [${navItems.map(item => item.id).join(', ')}]`);

  const showSubNavigation = false;

  // 로그아웃 핸들러 (JWT 토큰도 함께 삭제)
  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      // JWT 토큰도 함께 삭제
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('user');
      
      console.log('✅ [Navigation] 로그아웃 성공 (JWT 토큰 포함)');
      setShowUserMenu(false);
      
      // 게시판 페이지로 이동 후 새로고침
      navigate('/board');
      window.location.reload();
    }
  };

  // 사용자 메뉴 토글
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // 사용자 메뉴 외부 클릭 시 닫기
  const handleClickOutside = (e) => {
    if (!e.target.closest('.user-menu-container')) {
      setShowUserMenu(false);
    }
  };

  React.useEffect(() => {
    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserMenu]);

  // 로딩 상태에서도 기본 메뉴는 표시 (더 이상 네비게이션을 숨기지 않음)

  return (
    <>
      {/* 데스크톱/태블릿 상단 네비게이션 */}
      <nav className="top-nav">
        <div className="nav-container">
          <div className="nav-brand">
            <div className="brand-text">
              <span className="brand-js-planner"><span className="brand-capital-j">J</span>'s Planner</span>
            </div>
          </div>
          <ul className="nav-menu">
            {navItems.map(item => (
              <li key={item.id} className="nav-item">
                <Link
                  to={item.id === 'board' ? '/board' :
                      item.id === 'travel' ? '/travel' :
                      `/${item.id}`}
                  className={`nav-link ${currentPage === item.id ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          
          {/* 로그인/사용자 메뉴 */}
          <div className="auth-menu-container">
            {isAuthenticated ? (
              /* 로그인된 사용자 메뉴 */
              <div className="user-menu-container">
                <button 
                  className="user-menu-button"
                  onClick={toggleUserMenu}
                  aria-label="사용자 메뉴"
                >
                  <span className="user-avatar">
                    {isAdmin ? '👑' : '👤'}
                  </span>
                  <span className="user-name">{user?.nickname || user?.user_id || 'User'}</span>
                  <span className="user-menu-arrow">
                    {showUserMenu ? '▲' : '▼'}
                  </span>
                </button>
                
                {showUserMenu && (
                  <div className="user-dropdown">
                    <div className="user-info">
                      <div className="user-info-name">{user?.nickname || user?.user_id}</div>
                      <div className="user-info-role">
                        {isAdmin ? '👑 관리자' : '👤 일반 사용자'}
                      </div>
                      <div className="user-info-grade">
                        {formatGradeDisplay(currentUserGrade)}
                      </div>
                      <div className="user-info-email">{user?.email}</div>
                    </div>
                    <hr className="user-menu-divider" />
                    <Link 
                      to="/profile"
                      className="user-menu-button profile-button"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <span className="menu-icon">👤</span>
                      프로필
                    </Link>
                    <button 
                      className="user-menu-button logout-button"
                      onClick={handleLogout}
                    >
                      <span className="logout-icon">🚪</span>
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* 비로그인 사용자 로그인 버튼 */
              <Link 
                to="/login"
                className="login-button-nav"
                aria-label="로그인"
              >
                <span className="login-icon">🔐</span>
                <span className="login-text">로그인</span>
              </Link>
            )}
          </div>
        </div>
      </nav>


      {/* 모바일 하단 네비게이션 */}
      <nav className="bottom-nav">
        {navItems.map(item => (
          <Link
            key={item.id}
            to={item.id === 'board' ? '/board' :
                item.id === 'travel' ? '/travel' :
                `/${item.id}`}
            className={`bottom-nav-item ${currentPage === item.id ? 'active' : ''}`}
          >
            <span className="bottom-nav-icon">{item.icon}</span>
            <span className="bottom-nav-label">{item.label}</span>
          </Link>
        ))}
        
        {/* 모바일 로그인/사용자 메뉴 */}
        {isAuthenticated ? (
          <button
            className="bottom-nav-item user-menu-mobile"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleUserMenu();
            }}
          >
            <span className="bottom-nav-icon">
              {isAdmin ? '👑' : '👤'}
            </span>
            <span className="bottom-nav-label">
              {user?.nickname || 'User'}
            </span>
          </button>
        ) : (
          <Link
            to="/login"
            className="bottom-nav-item login-mobile"
          >
            <span className="bottom-nav-icon">🔐</span>
            <span className="bottom-nav-label">로그인</span>
          </Link>
        )}
      </nav>

      
      {/* 모바일/태블릿 사용자 메뉴 (바텀시트) - 1023px 이하에서만 표시 */}
      {showUserMenu && isAuthenticated && (
        <div
          className="mobile-user-menu-overlay mobile-only"
          onClick={() => setShowUserMenu(false)}
        >
          <div
            className="mobile-user-menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-user-info">
              <div className="mobile-user-avatar">
                {isAdmin ? '👑' : '👤'}
              </div>
              <div className="mobile-user-details">
                <div className="mobile-user-name">{user?.nickname || user?.user_id}</div>
                <div className="mobile-user-role">
                  {isAdmin ? '👑 관리자' : '👤 일반 사용자'}
                </div>
                <div className="mobile-user-grade">
                  {formatGradeDisplay(currentUserGrade)}
                </div>
                <div className="mobile-user-email">{user?.email}</div>
              </div>
            </div>
            <button
              className="mobile-user-button mobile-profile-button"
              onClick={() => {
                setShowUserMenu(false);
                navigate('/profile');
              }}
            >
              <span className="menu-icon">👤</span>
              프로필
            </button>
            <button 
              className="mobile-user-button mobile-logout-button"
              onClick={() => {
                handleLogout();
                setShowUserMenu(false);
              }}
            >
              <span className="logout-icon">🚪</span>
              로그아웃
            </button>
          </div>
        </div>
      )}
      
      {/* 로그인 모달 */}
      {showLoginModal && (
        <div className="login-modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="login-modal-close"
              onClick={() => setShowLoginModal(false)}
              aria-label="모달 닫기"
            >
              ✕
            </button>
            <LoginPage 
              onLoginSuccess={(user) => {
                console.log('네비게이션에서 로그인 성공:', user.name);
                setShowLoginModal(false);
              }}
              onSignupClick={() => {
                setShowLoginModal(false);
                navigate('/signup');
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;